import React, { useState, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Send, CheckCircle2, MessageSquare } from "lucide-react";
import { submitFeedback } from "../lib/payments";

const NOTE_COLORS = ["#FFE873", "#FFB4D8", "#B7F5C2", "#B5E2FF"];

// A sticky-note styled feedback form. The user writes on the "post-it",
// then drags (or clicks Send) it into the drop-box beside it.
export default function FeedbackDropZone({ section, title, subtitle }) {
  const [message, setMessage] = useState("");
  const [name, setName] = useState("");
  const [color, setColor] = useState(NOTE_COLORS[0]);
  const [flying, setFlying] = useState(false);
  const [sent, setSent] = useState(false);
  const [err, setErr] = useState("");
  const noteRef = useRef(null);
  const dropRef = useRef(null);

  // Manual drag-to-drop: we don't rely on HTML5 drag APIs (touch-hostile),
  // instead we animate the note flying toward the drop-box on release.
  const dragState = useRef({ active: false, startX: 0, startY: 0, ox: 0, oy: 0, pid: null });
  const [pos, setPos] = useState({ x: 0, y: 0 });

  const onPointerDown = (e) => {
    if (e.target.closest("textarea, input, button")) return;
    if (sent || flying) return;
    e.preventDefault();
    dragState.current = { active: true, startX: e.clientX, startY: e.clientY, ox: pos.x, oy: pos.y, pid: e.pointerId };
    try { noteRef.current?.setPointerCapture(e.pointerId); } catch { /* ignore */ }
  };
  const onPointerMove = (e) => {
    if (!dragState.current.active) return;
    setPos({ x: dragState.current.ox + (e.clientX - dragState.current.startX), y: dragState.current.oy + (e.clientY - dragState.current.startY) });
  };
  const onPointerUp = (e) => {
    if (!dragState.current.active) return;
    dragState.current.active = false;
    try { noteRef.current?.releasePointerCapture(dragState.current.pid); } catch { /* ignore */ }
    // Detect drop over box
    const noteBox = noteRef.current?.getBoundingClientRect();
    const dropBox = dropRef.current?.getBoundingClientRect();
    if (noteBox && dropBox) {
      const overlap =
        noteBox.right > dropBox.left &&
        noteBox.left < dropBox.right &&
        noteBox.bottom > dropBox.top &&
        noteBox.top < dropBox.bottom;
      if (overlap) {
        submit();
        return;
      }
    }
    // Snap back
    setPos({ x: 0, y: 0 });
  };

  const submit = async () => {
    const msg = message.trim();
    if (!msg) {
      setErr("Write something on the post-it first.");
      setPos({ x: 0, y: 0 });
      return;
    }
    setErr("");
    setFlying(true);
    try {
      await submitFeedback({ section, message: msg, name: name.trim() || null, color });
      setTimeout(() => setSent(true), 350);
    } catch (e) {
      setErr(e.message || "Couldn't send. Try again in a sec.");
      setFlying(false);
      setPos({ x: 0, y: 0 });
    }
  };

  return (
    <div className="mt-14 pt-10 border-t border-cyan-400/15" data-testid={`feedback-${section}`}>
      <div className="flex items-center gap-2 mb-2">
        <MessageSquare size={16} className="text-cyan-300" />
        <div className="text-xs uppercase tracking-[0.28em] text-cyan-300/80 font-heading">
          {title || "Leave AMC a note"}
        </div>
      </div>
      <p className="font-body text-slate-400 text-sm mb-6">
        {subtitle || "Write on the post-it, then drag it into the comments box — or just tap Send."}
      </p>

      <AnimatePresence mode="wait">
        {sent ? (
          <motion.div
            key="thanks"
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="rounded-2xl border border-cyan-400/30 bg-slate-900/50 backdrop-blur px-6 py-8 text-center"
            data-testid={`feedback-${section}-thanks`}
          >
            <CheckCircle2 size={28} className="mx-auto text-cyan-300 mb-2" />
            <div className="font-heading text-slate-100 text-lg mb-1">Note received — thank you.</div>
            <div className="font-body text-slate-400 text-sm">
              Every scribble helps shape the Complete Edition.
            </div>
          </motion.div>
        ) : (
          <motion.div
            key="form"
            className="grid grid-cols-1 md:grid-cols-[1fr_1fr] gap-6 items-start relative"
          >
            {/* Post-it note */}
            <motion.div
              ref={noteRef}
              onPointerDown={onPointerDown}
              onPointerMove={onPointerMove}
              onPointerUp={onPointerUp}
              onPointerCancel={onPointerUp}
              animate={
                flying
                  ? { x: (dropRef.current?.getBoundingClientRect().left || 0) - (noteRef.current?.getBoundingClientRect().left || 0) + 40, y: 0, rotate: -8, opacity: 0.2, scale: 0.7 }
                  : { x: pos.x, y: pos.y, rotate: -2, opacity: 1, scale: 1 }
              }
              transition={{ type: "spring", stiffness: 220, damping: 22 }}
              className="relative rounded-sm p-4 shadow-xl cursor-grab active:cursor-grabbing select-none"
              style={{ background: color, touchAction: "none", boxShadow: "0 12px 30px rgba(0,0,0,0.35)" }}
              data-testid={`feedback-${section}-note`}
            >
              <div className="flex items-center justify-between mb-2">
                <span className="text-[10px] font-mono uppercase tracking-widest text-slate-800/60">
                  Drag me →
                </span>
                <div className="flex gap-1">
                  {NOTE_COLORS.map((c) => (
                    <button
                      key={c}
                      type="button"
                      onClick={() => setColor(c)}
                      className={`w-4 h-4 rounded-full border ${color === c ? "border-slate-900" : "border-black/10"}`}
                      style={{ background: c }}
                      data-testid={`feedback-${section}-color-${c}`}
                      aria-label={`color ${c}`}
                    />
                  ))}
                </div>
              </div>
              <textarea
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder="What worked? What didn't? Anything missing?"
                data-testid={`feedback-${section}-message`}
                rows={5}
                className="w-full bg-transparent border-none outline-none resize-none text-slate-900 placeholder:text-slate-700/50 font-body text-[15px] leading-relaxed"
                style={{ fontFamily: "'Caveat', 'Kalam', 'Comic Sans MS', cursive" }}
              />
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="— your name (optional)"
                data-testid={`feedback-${section}-name`}
                className="w-full bg-transparent border-none outline-none text-slate-900 placeholder:text-slate-700/40 font-body text-sm"
                style={{ fontFamily: "'Caveat', 'Kalam', cursive" }}
              />
              <div className="flex items-center justify-end mt-2">
                <button
                  type="button"
                  onClick={submit}
                  disabled={flying}
                  data-testid={`feedback-${section}-send`}
                  className="inline-flex items-center gap-1.5 px-3 h-8 rounded-full bg-slate-900 text-cyan-200 text-xs font-heading uppercase tracking-widest hover:bg-slate-800 disabled:opacity-50"
                >
                  <Send size={12} /> Send
                </button>
              </div>
            </motion.div>

            {/* Drop zone */}
            <div
              ref={dropRef}
              className="rounded-2xl border-2 border-dashed border-cyan-400/40 bg-slate-950/40 min-h-[220px] flex flex-col items-center justify-center text-center p-6"
              data-testid={`feedback-${section}-dropbox`}
            >
              <div className="w-12 h-12 rounded-full bg-cyan-400/10 border border-cyan-400/40 flex items-center justify-center text-cyan-300 mb-3">
                <MessageSquare size={20} />
              </div>
              <div className="font-heading text-slate-100 text-sm mb-1">Comments box</div>
              <div className="font-body text-slate-400 text-xs max-w-[220px]">
                Drop your post-it here — or just hit <span className="text-cyan-200 font-mono">Send</span>. Every note lands straight in AMC&apos;s inbox.
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {err && (
        <div className="mt-3 text-sm text-rose-300" data-testid={`feedback-${section}-error`}>{err}</div>
      )}
    </div>
  );
}
